import React from 'react';
import { Box, Divider, HStack, Icon, Text } from '@chakra-ui/react';
import { FiExternalLink, FiPhone, FiCornerUpLeft } from 'react-icons/fi';

import Linkify from './Linkify';

/**
 * A WhatsApp template rendered from its definition and the values put into it.
 *
 * Two screens need exactly this: the bubble in the thread, reading the snapshot
 * stored on the message, and the picker's live preview, reading the mirror row
 * as an agent types. Same component, same substitution, so what the agent
 * approves in the preview is what the thread shows afterwards.
 *
 * ★ IT RENDERS A DEFINITION, NOT A SENT PAYLOAD.
 * `components` here is Meta's template definition — `{type:'BODY', text:'Hi
 * {{1}}'}` — not the parameter array that goes on the wire. The wire payload
 * carries only the values, with none of the copy around them, so it cannot be
 * rendered at all.
 *
 * ★ IT HANDLES NAMED PLACEHOLDERS, NOT ONLY NUMBERED ONES.
 * The template-authoring screen's own preview matches `{{n}}` alone. Fed a
 * NAMED template it leaves `{{first_name}}` on screen literally and then sends
 * correctly — a preview that disagrees with the message, which is the worst
 * shape this can take. Both forms substitute here.
 *
 * ★ A PLACEHOLDER WITH NO VALUE STAYS VISIBLE.
 * In the picker that is the point: the agent sees exactly where their text will
 * land. In the thread it cannot happen, because the server refuses a send with
 * a missing parameter.
 */

/** `{{1}}` and `{{first_name}}` alike. Meta allows letters, digits, underscore. */
const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

const upper = (value) => String(value ?? '').toUpperCase();

/**
 * Split one string into text and placeholder runs.
 *
 * Returned as parts rather than a joined string so an unfilled placeholder can
 * be drawn differently from the copy around it.
 *
 * @param {string} text
 * @param {(token: string) => string|undefined} resolve
 */
export function fillParts(text, resolve) {
	const source = String(text ?? '');
	const parts = [];
	let last = 0;

	// `exec` in a loop rather than `replace`, because the result is React nodes.
	PLACEHOLDER.lastIndex = 0;
	let match = PLACEHOLDER.exec(source);
	while (match) {
		if (match.index > last) parts.push({ text: source.slice(last, match.index) });
		const value = resolve(match[1]);
		parts.push(
			String(value ?? '').trim()
				? { text: String(value) }
				: { text: match[0], placeholder: true, token: match[1] },
		);
		last = match.index + match[0].length;
		match = PLACEHOLDER.exec(source);
	}
	if (last < source.length) parts.push({ text: source.slice(last) });
	return parts;
}

/** The same thing as a plain string — for titles, previews and copy. */
export function fillText(text, resolve) {
	return fillParts(text, resolve)
		.map((part) => part.text)
		.join('');
}

/**
 * Pull the four pieces out of Meta's `components` array.
 *
 * Meta uppercases its types; the mirror stores whatever it received, so both
 * cases are accepted rather than assumed.
 */
export function splitComponents(components = []) {
	const list = Array.isArray(components) ? components : [];
	const of = (type) => list.find((c) => upper(c?.type) === type) ?? null;
	const buttons = of('BUTTONS');
	return {
		header: of('HEADER'),
		body: of('BODY'),
		footer: of('FOOTER'),
		buttons: Array.isArray(buttons?.buttons) ? buttons.buttons : [],
	};
}

/**
 * How a value key is spelled, per component.
 *
 * ★ EACH COMPONENT NUMBERS ITS OWN PLACEHOLDERS.
 * A header reading `Hello {{1}}` means the HEADER's first parameter, which the
 * value map calls `header` — not body parameter 1. Resolving them through one
 * flat lookup would put the body's first value in the header.
 */
const resolvers = (values = {}) => ({
	header: () => values.header,
	body: (token) => values[token],
	button: (index) => () => values[`button:${index}`],
});

const Placeholder = ({ children }) => (
	<Box
		as="span"
		px="1"
		borderRadius="sm"
		bg="wa.chip"
		color="wa.inkMuted"
		fontStyle="italic"
		fontSize="0.95em"
	>
		{children}
	</Box>
);

const Filled = ({ parts }) => (
	<>
		{parts.map((part, i) =>
			part.placeholder ? (
				<Placeholder key={`p${i}`}>{part.text}</Placeholder>
			) : (
				<Linkify key={`t${i}`} text={part.text} />
			),
		)}
	</>
);

const BUTTON_ICON = { URL: FiExternalLink, PHONE_NUMBER: FiPhone, QUICK_REPLY: FiCornerUpLeft };

/**
 * The button strip, separate so a caller can put something between it and the
 * text.
 *
 * ★ THE BUBBLE NEEDS THAT. WhatsApp puts the timestamp and ticks ABOVE the
 * button divider, not under the last button — drawn in one block the time ends
 * up looking like part of the buttons.
 *
 * Nothing here is clickable: this is a picture of what the customer sees in
 * their app, not a control in ours.
 */
export function TemplateButtons({ components, values = {} }) {
	const { buttons } = React.useMemo(() => splitComponents(components), [components]);
	const resolve = React.useMemo(() => resolvers(values), [values]);
	if (!buttons.length) return null;

	return (
		<Box mt="1" mx="-2" mb="-1">
			{buttons.map((button, index) => {
				const label = String(button?.text ?? '').trim() || 'Button';
				return (
					<React.Fragment key={`${label}-${index}`}>
						<Divider borderColor="wa.divider" />
						<HStack spacing="1.5" justify="center" py="1.5" color="wa.accent">
							<Icon as={BUTTON_ICON[upper(button?.type)] ?? FiExternalLink} boxSize="13px" />
							<Text fontSize="13px" fontWeight="medium" noOfLines={1}>
								{/* A dynamic URL button's variable lives in its url, but the
								    label is what is shown, so only it is filled. */}
								{fillText(label, resolve.button(index))}
							</Text>
						</HStack>
					</React.Fragment>
				);
			})}
		</Box>
	);
}

/**
 * @param {object[]} components  Meta's template definition
 * @param {object}   values      key → string, as the server validated them
 */
export default function TemplateParts({ components, values = {}, showButtons = true }) {
	const { header, body, footer, buttons } = React.useMemo(() => splitComponents(components), [components]);
	const resolve = React.useMemo(() => resolvers(values), [values]);

	// A media header carries no text to render. Saying so beats drawing nothing
	// and leaving the reader wondering whether the message had an image.
	const headerFormat = upper(header?.format || 'TEXT');
	const headerIsMedia = header && headerFormat !== 'TEXT';

	if (!header && !body && !footer && !buttons.length) return null;

	return (
		<Box fontSize="14.2px" lineHeight="19px" wordBreak="break-word">
			{headerIsMedia && (
				<Text fontSize="xs" color="wa.inkMuted" mb="1">
					{headerFormat.toLowerCase()} attachment
				</Text>
			)}

			{header && !headerIsMedia && header.text && (
				<Text fontWeight="semibold" mb="1" whiteSpace="pre-wrap">
					<Filled parts={fillParts(header.text, resolve.header)} />
				</Text>
			)}

			{body?.text && (
				<Text whiteSpace="pre-wrap">
					<Filled parts={fillParts(body.text, resolve.body)} />
				</Text>
			)}

			{footer?.text && (
				<Text fontSize="xs" color="wa.inkMuted" mt="1" whiteSpace="pre-wrap">
					{footer.text}
				</Text>
			)}

			{showButtons && <TemplateButtons components={components} values={values} />}
		</Box>
	);
}
